import { FormControlLabel, Grid, Switch, TextField, Typography } from '@mui/material';
import React, { useEffect, useRef } from 'react';
import { Hospital } from 'types/hospital';
import { HospitalValidation } from '../validation/useHospitalValidation';
import CnpjInput from 'components/masked-input/CnpjInput';
import { RecordSection } from 'components/record-shell/RecordShell';
import { asInputComponent } from 'components/masked-input/asInputComponent';

interface HospitalFormProps {
  hospital: Hospital;
  handleChange: <K extends keyof Hospital>(index: K, value: Hospital[K]) => void;
  validation: HospitalValidation;
}

const HospitalForm: React.FC<HospitalFormProps> = ({ hospital, handleChange, validation }) => {
  const inputs = {
    company_name: useRef<HTMLInputElement>(null),
    company_trade_name: useRef<HTMLInputElement>(null),
    document_number: useRef<HTMLInputElement>(null),
  };

  useEffect(() => {
    const [key] = Object.keys(validation) as [keyof typeof inputs];

    if (!key) return;

    inputs[key].current?.focus();
  }, [validation]);

  return (
    <Grid container size={12} spacing={2} maxWidth={900}>
      <Grid size={12}>
        <Typography variant="subtitle2" fontWeight={700} color="primary">
          Identificação
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, md: 8 }}>
        <TextField
          fullWidth
          inputRef={inputs.company_name}
          label="Razão social"
          placeholder="Digite a razão social"
          value={hospital.company_name}
          onChange={e => handleChange('company_name', e.target.value)}
          error={!!validation.company_name}
          helperText={validation.company_name}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          inputRef={inputs.document_number}
          error={!!validation.document_number}
          helperText={validation.document_number}
          label="CNPJ"
          placeholder="Digite o CNPJ"
          value={hospital.document_number}
          onChange={e => handleChange('document_number', e.target.value)}
          slotProps={{
            input: {
              inputComponent: asInputComponent(CnpjInput),
            },
          }}
        />
      </Grid>

      <Grid size={12}>
        <TextField
          fullWidth
          inputRef={inputs.company_trade_name}
          label="Nome fantasia"
          placeholder="Digite o nome fantasia"
          value={hospital.company_trade_name}
          onChange={e => handleChange('company_trade_name', e.target.value)}
          error={!!validation.company_trade_name}
          helperText={validation.company_trade_name}
        />
      </Grid>

      <Grid size={12}>
        <RecordSection title="Contato">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="E-mail"
                placeholder="Digite o e-mail"
                onChange={e => handleChange('email', e.target.value)}
                value={hospital.email ?? ''}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Telefone"
                placeholder="Digite o telefone"
                onChange={e => handleChange('phone', e.target.value)}
                value={hospital.phone ?? ''}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                label="Telefone fixo"
                placeholder="Digite o telefone fixo"
                onChange={e => handleChange('fixed_phone', e.target.value)}
                value={hospital.fixed_phone ?? ''}
              />
            </Grid>
          </Grid>
        </RecordSection>
      </Grid>

      <Grid size={12}>
        <RecordSection title="Informações">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Setor"
                placeholder="Ex.: Centro cirúrgico"
                onChange={e => handleChange('sector', e.target.value)}
                value={hospital.sector ?? ''}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 8 }}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Observações"
                placeholder="Informações úteis para a equipe"
                onChange={e => handleChange('observation', e.target.value)}
                value={hospital.observation ?? ''}
              />
            </Grid>
          </Grid>
        </RecordSection>
      </Grid>

      <Grid size={12}>
        <FormControlLabel
          label="Ativo"
          control={
            <Switch checked={!!hospital.active} onChange={e => handleChange('active', e.target.checked ? 1 : 0)} />
          }
        />
      </Grid>
    </Grid>
  );
};

export default HospitalForm;
